export interface TemplateFolderInput {
  id: string;
  category?: string;
  name?: string;
  description?: string;
  tags?: string[];
}

export interface TemplateFolder {
  id: string;
  label: string;
  order: number;
}

export const TEMPLATE_FOLDERS: TemplateFolder[] = [
  { id: "dashboards", label: "Dashboards", order: 10 },
  { id: "smart-home", label: "Smart Home", order: 20 },
  { id: "industrial-monitoring", label: "Industrial Monitoring", order: 30 },
  { id: "project-management", label: "Project Management", order: 40 },
  { id: "files-documents", label: "Files & Documents", order: 50 },
  { id: "charts-metrics", label: "Charts & Metrics", order: 60 },
  { id: "maps-location", label: "Maps & Location", order: 70 },
  { id: "forms-decisions", label: "Forms & Decisions", order: 80 },
  { id: "commerce-marketing", label: "Commerce & Marketing", order: 90 },
  { id: "people-teams", label: "People & Teams", order: 100 },
  { id: "navigation-layout", label: "Navigation & Layout", order: 110 },
  { id: "controls-iot", label: "Controls & IoT", order: 120 },
  { id: "content-activity", label: "Content & Activity", order: 130 },
  { id: "atomic-components", label: "Atomic Components", order: 140 },
  { id: "misc", label: "Miscellaneous", order: 1000 },
];

const folderById = new Map(
  TEMPLATE_FOLDERS.map((folder) => [folder.id, folder]),
);

const includesAny = (value: string, words: string[]) =>
  words.some((word) => value.includes(word));

export function getTemplateFolder(input: TemplateFolderInput): TemplateFolder {
  const haystack = [
    input.id,
    input.category,
    input.name,
    input.description,
    ...(input.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const folderId = includesAny(haystack, [
    "home-assistant",
    "smart-home",
    "lighting",
    "climate",
    "security",
    "energy",
    "garden",
    "automation",
  ])
    ? "smart-home"
    : includesAny(haystack, [
          "pressure",
          "flow",
          "depth",
          "sensor",
          "monitoring",
          "fermentation",
          "tank",
          "barn",
          "well",
          "weather",
        ])
      ? "industrial-monitoring"
      : includesAny(haystack, [
            "pm-",
            "project",
            "sprint",
            "kanban",
            "burndown",
            "velocity",
            "release",
            "timeline",
            "workload",
            "timesheet",
            "time-tracker",
            "calendar",
            "milestone",
          ])
        ? "project-management"
        : includesAny(haystack, [
              "file-storage",
              "document",
              "image gallery",
              "documents",
              "files",
            ])
          ? "files-documents"
          : includesAny(haystack, [
                "chart",
                "metric",
                "kpi",
                "gauge",
                "statistics",
                "trend",
                "treemap",
                "dashboard-grid",
                "percentage",
                "currency",
              ])
            ? "charts-metrics"
            : includesAny(haystack, [
                  "map",
                  "location",
                  "zone",
                  "geospatial",
                  "soil-moisture-map",
                ])
              ? "maps-location"
              : includesAny(haystack, [
                    "form",
                    "questionnaire",
                    "voting",
                    "poll",
                    "confirmation",
                    "decision",
                  ])
                ? "forms-decisions"
                : includesAny(haystack, [
                      "pricing",
                      "competitive",
                      "channel",
                      "content-marketing",
                      "growth",
                      "strategy",
                      "audience",
                    ])
                  ? "commerce-marketing"
                  : includesAny(haystack, [
                        "team",
                        "assignment",
                        "owner",
                        "user",
                        "avatar",
                      ])
                    ? "people-teams"
                    : includesAny(haystack, [
                          "breadcrumb",
                          "sidebar",
                          "header",
                          "layout",
                          "two-column",
                        ])
                      ? "navigation-layout"
                      : includesAny(haystack, [
                            "control",
                            "button",
                            "slider",
                            "toggle",
                            "stepper",
                            "thermostat",
                            "status-light",
                            "radial",
                          ])
                        ? "controls-iot"
                        : includesAny(haystack, [
                              "activity",
                              "feed",
                              "faq",
                              "notification",
                              "alert",
                              "catalog",
                              "accordion",
                            ])
                          ? "content-activity"
                          : input.category === "atoms"
                            ? "atomic-components"
                            : input.category === "pages"
                              ? "dashboards"
                              : "misc";

  return folderById.get(folderId) ?? folderById.get("misc")!;
}
